import type { FC } from 'react';
import { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AppPlatform, type AIFormState } from '@trinity/chat';
import { ChatLoggerContext } from '@trinity/chat-components/containers/Chat';
import type { apiSchemas } from '@trinity/management-app-client';
import { useAppPlatform } from '../../../containers/Chat/hooks';
import { DynamicFormWizard } from '../DynamicForm.wizard';
import { FormDisclaimerModal } from './FormDisclaimerModal';
import { syncDynamicFormToIConnect } from './sync';
import type { FormWorkflowIntroConfig } from './types';
import { useIconnectArtifactDownloader } from './useIconnectArtifactDownloader';

type FormSchema = apiSchemas.FormSchema;
type Step = 'initial' | 'manual-entry' | 'submitted' | 'cancelled';

interface IconnectAwareFormWorkflowProps {
  queryId: number;
  queryIdentifier?: string;
  workflowType: string;
  value: Record<string, unknown> & {
    workflowType?: string;
    formState?: Step;
    draftId?: string;
    showButtons?: boolean;
    isFromSpeech?: boolean;
    formConfig?: FormSchema;
    aiState?: AIFormState;
    submittedData?: {
      formData?: Array<{
        fieldLabel: string;
        fieldType: string;
        value: unknown;
      }>;
    };
    submittedAt?: string | number | Date;
    introConfig?: FormWorkflowIntroConfig;
  };
  introConfig: FormWorkflowIntroConfig;
}

export const IconnectAwareFormWorkflow: FC<IconnectAwareFormWorkflowProps> = memo((props) => {
  const appPlatform = useAppPlatform();
  const disclaimerEnabled = props.introConfig.disclaimerEnabled ?? false;
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const pendingSubmitWithSyncRef = useRef<null | (() => Promise<void>)>(null);
  const pendingSubmitOnlyRef = useRef<null | (() => Promise<void>)>(null);
  const formConfig = props.value.formConfig;
  const iconnectSyncConfig = props.introConfig.iconnectSync;
  const isIConnectRuntime = appPlatform === AppPlatform.enum.veeva;
  const canSyncToIConnect =
    isIConnectRuntime && !!iconnectSyncConfig?.enabled && !!iconnectSyncConfig?.mapping && !!formConfig;

  const loggerCtx = useContext(ChatLoggerContext);

  const downloadArtifact = useIconnectArtifactDownloader();
  const [capturedDashboardImage, setCapturedDashboardImage] = useState<string | undefined>();

  const downloadPdfArtifact = useCallback(
    (draftId?: string) => {
      const resolvedDraftId = draftId ?? props.value.draftId;
      if (!resolvedDraftId) {
        return;
      }

      downloadArtifact({
        draftId: resolvedDraftId,
        dashboardImageBase64: capturedDashboardImage,
      }).catch((e) => {
        loggerCtx.captureException(e);
      });
    },
    [downloadArtifact, props.value.draftId, loggerCtx, capturedDashboardImage]
  );
  const openDisclaimer = useCallback(() => {
    setIsDisclaimerOpen(true);
  }, []);

  const closeDisclaimer = useCallback(() => {
    setIsDisclaimerOpen(false);
    pendingSubmitWithSyncRef.current = null;
    pendingSubmitOnlyRef.current = null;
  }, []);

  const submitBehavior = useMemo(() => {
    return {
      onBeforeSubmit: async (dynamicFormData: Record<string, unknown>) => {
        if (!formConfig) {
          return;
        }

        if (!canSyncToIConnect) {
          return;
        }

        await syncDynamicFormToIConnect({
          formConfig,
          dynamicFormData,
          introConfig: props.introConfig,
        });
      },
      onRequestSubmit: async (controls: {
        submitWithPreSubmit: () => Promise<{ draftId?: string; submittedAt?: string } | void>;
        submitOnly: () => Promise<{ draftId?: string; submittedAt?: string } | void>;
        dynamicFormData: Record<string, unknown>;
      }) => {
        if (!disclaimerEnabled) {
          const result = await controls.submitWithPreSubmit();
          const draftId = result && typeof result === 'object' ? result.draftId : undefined;
          downloadPdfArtifact(draftId);
          return;
        }

        pendingSubmitWithSyncRef.current = async () => {
          const result = await controls.submitWithPreSubmit();
          const draftId = result && typeof result === 'object' ? result.draftId : undefined;
          downloadPdfArtifact(draftId);
        };
        pendingSubmitOnlyRef.current = async () => {
          const result = await controls.submitOnly();
          const draftId = result && typeof result === 'object' ? result.draftId : undefined;
          downloadPdfArtifact(draftId);
        };
        openDisclaimer();
      },
    };
  }, [
    canSyncToIConnect,
    disclaimerEnabled,
    downloadPdfArtifact,
    formConfig,
    openDisclaimer,
    props.introConfig,
  ]);

  return (
    <>
      <DynamicFormWizard
        queryId={props.queryId}
        queryIdentifier={props.queryIdentifier}
        workflowType={props.workflowType}
        value={props.value}
        introConfig={props.introConfig}
        submitBehavior={submitBehavior}
        onDashboardCapture={setCapturedDashboardImage}
      />
      {disclaimerEnabled && (
        <FormDisclaimerModal
          open={isDisclaimerOpen}
          onClose={closeDisclaimer}
          onSyncToIConnect={async () => {
            const submit = pendingSubmitWithSyncRef.current;
            closeDisclaimer();
            if (submit) {
              await submit();
            }
          }}
          onSubmit={async () => {
            const submit = pendingSubmitOnlyRef.current;
            closeDisclaimer();
            if (submit) {
              await submit();
            }
          }}
          disclaimerEnabled={disclaimerEnabled}
          showSyncToIConnect={canSyncToIConnect}
          disclaimerTitle={props.introConfig.disclaimerTitle}
          disclaimerText={props.introConfig.disclaimerText}
          syncToIconnectButtonText={iconnectSyncConfig?.syncToIconnectButtonText}
          submitButtonText={props.introConfig.submitButtonText}
        />
      )}
    </>
  );
});
